import type {IAction} from '../types';
import {actionsToOrderedActions} from './actionsToOrderedActions';

export type ParseActionsCommonOptions = {
	sort?: boolean;
	order?: boolean;
	omit?: string[] | ((action: IAction) => boolean);
	pick?: string[] | ((action: IAction) => boolean);
};

const shouldOmit = (
	action: IAction,
	omit?: string[] | ((action: IAction) => boolean),
) => {
	if (!omit) return false;
	return typeof omit === 'function' ? omit(action) : omit.includes(action.name);
};

const shouldPick = (
	action: IAction,
	pick?: string[] | ((action: IAction) => boolean),
) => {
	if (!pick) return true;
	return typeof pick === 'function' ? pick(action) : pick.includes(action.name);
};

export interface ParseActionsParams extends ParseActionsCommonOptions {
	actions: IAction[];
	args?: any[];
}

export const parseActions = ({
	actions,
	args,
	order = true,
	omit,
	pick,
	sort = true,
}: ParseActionsParams): Array<IAction | IAction[]> => {
	const filtered = actions.filter((action) => {
		if (shouldOmit(action, omit)) return false;
		if (!shouldPick(action, pick)) return false;
		if (
			action.enableTest &&
			action.enableTest.apply(action.enableTest, args ?? []) === false
		)
			return false;
		return true;
	});

	const parsed = filtered.reduce<IAction[]>((acc, action) => {
		if (action.children?.length) {
			const children = parseActions({
				actions: action.children as IAction[],
				args,
				order,
				omit,
				pick,
			});
			return !children.flat().length
				? acc
				: [
						...acc,
						{
							...action,
							children: children as any,
						},
					];
		}
		return [...acc, action];
	}, []);

	return order ? actionsToOrderedActions(parsed, sort === false) : parsed;
};
